import { memo } from "react";

import { formatDuration } from "util/util";
import { useGlobalScopeState } from "stores/ScopeStateStore";
import {
  BigBoardSortOrder,
  useBigBoardPreferencesStore,
} from "stores/UserPreferencesStore";
import { useRedirectIfNonAdmin } from "stores/NavStore";

import { ScopeType } from "sockapi/scope-types";
import { TeamSummaryState } from "sockapi/scope-specs/team-list-spec";
import {
  DirtyStateType,
  DirtyState,
  ServerDirtyState,
  IntervalMetricsSummary,
  getIntervalMetricsSummaryString,
} from "sockapi/scope-specs/server-health-spec";

import { LargeSpinner, Spinner } from "components/Loading";
import { useSortedTeams } from "pages/TeamListPage";
import Tooltip from "components/Tooltip";
import {
  TeamListTeamName,
  BigBoardHeader,
  BigBoardTeamSearch,
  BigBoardColumnData,
  BigBoardPageSelection,
  useCreateBigBoardPageTeamIds,
} from "pages/BigBoardPage";

import "./ServerHealthPage.css";

const FAST_SYNC_DANGER_THRESHOLD = 10 * 1000; // 10s
const SLOW_SYNC_DANGER_THRESHOLD = 5 * 60 * 1000; // 5min

const getDirtyStateEmoji = (dirtyStateType: DirtyStateType): string => {
  switch (dirtyStateType) {
    case DirtyStateType.NEW:
      return " ";
    case DirtyStateType.DIRTY:
      return "⚠️";
    case DirtyStateType.SYNCING:
      return "⏳";
    case DirtyStateType.FAILED:
      return "💥";
    case DirtyStateType.SYNCED:
      return "✅";
  }
};

const isDirtyStateSyncTimeSignificant = (
  dirtyStateType: DirtyStateType
): boolean => {
  return ![DirtyStateType.NEW, DirtyStateType.SYNCED].includes(dirtyStateType);
};

function DirtyStateCellNoMemo({
  dirtyState = { type: DirtyStateType.NEW, lastSyncTime: 0 },
  isSummary = false,
  isFast = false,
}: {
  dirtyState?: DirtyState;
  isSummary?: boolean;
  isFast?: boolean;
}) {
  const emoji = getDirtyStateEmoji(dirtyState.type);
  const timeSinceSync = Date.now() - dirtyState.lastSyncTime;
  const showTime = isDirtyStateSyncTimeSignificant(dirtyState.type);
  const isDangerous =
    showTime &&
    timeSinceSync >
      (isFast ? FAST_SYNC_DANGER_THRESHOLD : SLOW_SYNC_DANGER_THRESHOLD);
  return (
    <td
      className={`big-board-dirty-state-cell${isSummary ? " summary" : ""}${
        isDangerous ? " danger" : ""
      }`}
      title={
        showTime ? `Last sync: ${formatDuration(timeSinceSync)} ago` : undefined
      }
    >
      {emoji}
    </td>
  );
}
const DirtyStateCell = memo(DirtyStateCellNoMemo);

function IntervalMetricsCell({
  numPerSecond,
  isSummary = false,
}: {
  numPerSecond: number;
  isSummary?: boolean;
}) {
  return (
    <td
      className={`big-board-interval-metrics-cell${
        isSummary ? " summary" : ""
      }`}
    >
      {Math.round(numPerSecond * 10) <= 0 ? null : numPerSecond.toFixed(1)}
    </td>
  );
}

type NormalizedServerDirtyState = {
  [teamId: string]: {
    [colKey: string]: DirtyState | undefined;
  };
};

const getNormalizedDirtyState = (
  allTeams: TeamSummaryState[],
  dirtyState: ServerDirtyState
): NormalizedServerDirtyState => {
  return Object.fromEntries(
    allTeams.map(({ teamId }) => {
      const teamState = dirtyState.teams[teamId];
      const { fastSync, misc } = teamState ?? {};
      const teamDirtyState: { [colKey: string]: DirtyState | undefined } = {
        fastSync,
        misc,
      };
      return [teamId, teamDirtyState];
    })
  );
};

const updateDirtyStateSummary = (
  dirtyStateSummary: DirtyState,
  dirtyState: DirtyState
): DirtyState => {
  const dirtyStateTypeSignificanceOrder = [
    DirtyStateType.NEW,
    DirtyStateType.SYNCED,
    DirtyStateType.DIRTY,
    DirtyStateType.FAILED,
    DirtyStateType.SYNCING,
  ];
  if (
    dirtyStateTypeSignificanceOrder.indexOf(dirtyState.type) >
    dirtyStateTypeSignificanceOrder.indexOf(dirtyStateSummary.type)
  ) {
    dirtyStateSummary.type = dirtyState.type;
  }
  if (
    isDirtyStateSyncTimeSignificant(dirtyState.type) &&
    (dirtyStateSummary.lastSyncTime === 0 ||
      dirtyState.lastSyncTime < dirtyStateSummary.lastSyncTime)
  ) {
    dirtyStateSummary.lastSyncTime = dirtyState.lastSyncTime;
  }
  return dirtyStateSummary;
};

const getDirtyStateMetrics = (
  normalizedDirtyState: NormalizedServerDirtyState
) => {
  const dirtyStateCounts: { [dirtyStateType in DirtyStateType]?: number } = {};
  const teamDirtyStateSummaries: { [teamId: string]: DirtyState } = {};
  const colDirtyStateSummaries: { [colKey: string]: DirtyState } = {};
  for (const [teamId, teamState] of Object.entries(normalizedDirtyState)) {
    for (const [colKey, dirtyState] of Object.entries(teamState)) {
      if (dirtyState === undefined) continue;
      dirtyStateCounts[dirtyState.type] =
        (dirtyStateCounts[dirtyState.type] ?? 0) + 1;
      teamDirtyStateSummaries[teamId] = updateDirtyStateSummary(
        teamDirtyStateSummaries[teamId] ?? {
          type: DirtyStateType.NEW,
          lastSyncTime: 0,
        },
        dirtyState
      );
      colDirtyStateSummaries[colKey] = updateDirtyStateSummary(
        colDirtyStateSummaries[colKey] ?? {
          type: DirtyStateType.NEW,
          lastSyncTime: 0,
        },
        dirtyState
      );
    }
  }
  return {
    dirtyStateCounts,
    teamDirtyStateSummaries,
    colDirtyStateSummaries,
  };
};

function IntervalMetricsSummaryDisplay(props: {
  label: string;
  metrics: IntervalMetricsSummary;
}) {
  const { label, metrics } = props;
  return (
    <div className="health-metrics-summary">
      {label}:{" "}
      <span className="health-metrics-summary-string">
        {getIntervalMetricsSummaryString(metrics)}
      </span>
    </div>
  );
}

function ServerHealthRightDisplay() {
  const serverHealth = useGlobalScopeState(ScopeType.SERVER_HEALTH);

  if (serverHealth === null) return <Spinner inline />;

  const {
    report: { miscLocks },
  } = serverHealth;

  return (
    <>
      <div className="health-step-locks">
        <h4>Most contended misc locks</h4>
        {miscLocks.length === 0 ? (
          "No locks contended!"
        ) : (
          <div className="health-step-locks-list">
            {miscLocks.map(({ id, numActive }) => {
              return (
                <div key={id}>
                  (<b>{numActive}</b>) {id}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}

function ServerHealthPage() {
  const sortOrder = useBigBoardPreferencesStore((state) => state.sortOrder);
  const setSortOrder = useBigBoardPreferencesStore(
    (state) => state.setSortOrder
  );
  const serverHealth = useGlobalScopeState(ScopeType.SERVER_HEALTH);
  // Include hidden teams for this page as they contribute
  // to server load too.
  const allTeams = useSortedTeams(true);
  const createTeamIds = useCreateBigBoardPageTeamIds(true);

  useRedirectIfNonAdmin();

  if (allTeams === null || serverHealth === null || createTeamIds === null)
    return <LargeSpinner />;

  const {
    report: {
      fastSyncMetrics,
      slowSyncMetrics,
      checkAnswerMetrics,
      fastSyncQueueLength,
      numAuthsMetrics,
      numRequestsMetrics,
      teamNumAuthsMetrics,
      teamNumRequestsMetrics,
      numActivePeriodicTasksHandlers,
      numActiveConns,
      downloadLockContention,
      syncLockContention,
      userTimeUsage,
      systemTimeUsage,
    },
  } = serverHealth;

  const normalizedDirtyState = getNormalizedDirtyState(
    allTeams,
    serverHealth.dirtyState
  );
  const dirtyStateMetrics = getDirtyStateMetrics(normalizedDirtyState);
  const { dirtyStateCounts, teamDirtyStateSummaries, colDirtyStateSummaries } =
    dirtyStateMetrics;

  const sortFunc = (() => {
    switch (sortOrder) {
      case BigBoardSortOrder.LEADERBOARD:
        return undefined;
      case BigBoardSortOrder.HEALTH:
        return (teamId1: string, teamId2: string) => {
          const summary1 = teamDirtyStateSummaries[teamId1];
          const summary2 = teamDirtyStateSummaries[teamId2];
          const lastSyncTime1 = summary1?.lastSyncTime ?? 0;
          const lastSyncTime2 = summary2?.lastSyncTime ?? 0;
          const isDirtyDiff =
            (lastSyncTime1 > 0 ? 0 : 1) - (lastSyncTime2 > 0 ? 0 : 1);
          if (isDirtyDiff !== 0) return isDirtyDiff;
          if (lastSyncTime1 === 0 && lastSyncTime2 === 0) {
            const hasSync1 =
              (summary1?.type ?? DirtyStateType.NEW) !== DirtyStateType.NEW;
            const hasSync2 =
              (summary2?.type ?? DirtyStateType.NEW) !== DirtyStateType.NEW;
            return (hasSync1 ? 0 : 1) - (hasSync2 ? 0 : 1);
          }
          return lastSyncTime1 - lastSyncTime2;
        };
      case BigBoardSortOrder.ACTIVITY:
        return (teamId1: string, teamId2: string) => {
          const numPerInterval1 =
            teamNumRequestsMetrics[teamId1]?.numPerInterval ?? 0;
          const numPerInterval2 =
            teamNumRequestsMetrics[teamId2]?.numPerInterval ?? 0;
          return numPerInterval2 - numPerInterval1;
        };
    }
  })();
  const teamIds = createTeamIds(sortFunc);

  const getTeamNumAuthsPerSecond = (teamId: string): number => {
    const metrics = teamNumAuthsMetrics[teamId];
    if (metrics === undefined) return 0;
    const { intervalLength, numPerInterval } = metrics;
    return numPerInterval / (intervalLength / 1000);
  };
  const getTeamNumRequestsPerSecond = (teamId: string): number => {
    const metrics = teamNumRequestsMetrics[teamId];
    if (metrics === undefined) return 0;
    const { intervalLength, numPerInterval } = metrics;
    return numPerInterval / (intervalLength / 1000);
  };
  const metricsSummaries = {
    numAuthsPerSecond: 0,
    numRequestsPerSecond: 0,
  };
  for (const teamId of Object.keys(teamNumAuthsMetrics)) {
    metricsSummaries.numAuthsPerSecond += getTeamNumAuthsPerSecond(teamId);
  }
  for (const teamId of Object.keys(teamNumRequestsMetrics)) {
    metricsSummaries.numRequestsPerSecond +=
      getTeamNumRequestsPerSecond(teamId);
  }
  const cols: BigBoardColumnData[] = [
    { colKey: "fastSync", displayName: "Fast Sync", shortName: "Fast" },
    { colKey: "misc", displayName: "Misc. state", shortName: "Misc" },
  ];

  const rowsDom = teamIds.map((teamId) => {
    const teamNumAuthsPerSecond = getTeamNumAuthsPerSecond(teamId);
    const teamNumRequestsPerSecond = getTeamNumRequestsPerSecond(teamId);
    return (
      <tr key={teamId}>
        <td>
          <TeamListTeamName teamId={teamId} />
        </td>
        <td>{teamId}</td>
        <IntervalMetricsCell numPerSecond={teamNumAuthsPerSecond} />
        <IntervalMetricsCell numPerSecond={teamNumRequestsPerSecond} />
        <DirtyStateCell
          isSummary={true}
          dirtyState={teamDirtyStateSummaries[teamId]}
        />
        {cols.map((colData) => (
          <DirtyStateCell
            key={colData.colKey}
            dirtyState={normalizedDirtyState[teamId][colData.colKey]}
            isFast={colData.colKey === "fastSync"}
          />
        ))}
      </tr>
    );
  });

  return (
    <>
      <div className="big-board">
        <div className="health-left">
          <table>
            <colgroup>
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col />
              <col className="big-board-group-end" />
            </colgroup>
            <thead>
              <tr className="big-board-header-groups">
                <th colSpan={8} />
              </tr>
              <tr>
                <th>Team</th>
                <th>ID</th>
                <th>Auths / s</th>
                <th>Reqs / s</th>
                <th>[Summ.]</th>
                {cols.map((colData) => (
                  <BigBoardHeader key={colData.colKey} colData={colData} />
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>[Summary]</td>
                <td />
                <IntervalMetricsCell
                  numPerSecond={metricsSummaries.numAuthsPerSecond}
                  isSummary={true}
                />
                <IntervalMetricsCell
                  numPerSecond={metricsSummaries.numRequestsPerSecond}
                  isSummary={true}
                />
                <td />
                {cols.map((colData) => (
                  <DirtyStateCell
                    key={colData.colKey}
                    isSummary={true}
                    dirtyState={colDirtyStateSummaries[colData.colKey]}
                    isFast={colData.colKey === "fastSync"}
                  />
                ))}
              </tr>
              {rowsDom}
            </tbody>
          </table>
        </div>
        <div className="health-right">
          <ServerHealthRightDisplay />
        </div>
      </div>
      <div className="health-footer">
        <div className="health-footer-metrics">
          <IntervalMetricsSummaryDisplay
            label="Fast"
            metrics={fastSyncMetrics}
          />
          <IntervalMetricsSummaryDisplay
            label="Slow"
            metrics={slowSyncMetrics}
          />
          <IntervalMetricsSummaryDisplay
            label="Ans"
            metrics={checkAnswerMetrics}
          />
          <IntervalMetricsSummaryDisplay
            label="Auths"
            metrics={numAuthsMetrics}
          />
          <IntervalMetricsSummaryDisplay
            label="Reqs"
            metrics={numRequestsMetrics}
          />
        </div>
        <div className="health-footer-bottom">
          <BigBoardTeamSearch />
          <BigBoardPageSelection />
          <div className="big-board-sort-selection">
            <label htmlFor="big-board-sort-order">Sort by:</label>
            <select
              id="big-board-sort-order"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value as BigBoardSortOrder);
              }}
            >
              {Object.values(BigBoardSortOrder).map((order) => (
                <option key={order} value={order}>
                  {order}
                </option>
              ))}
            </select>
          </div>
          <div className="health-footer-counts">
            <Tooltip text="Dirty objects">
              {getDirtyStateEmoji(DirtyStateType.DIRTY)}{" "}
              {dirtyStateCounts[DirtyStateType.DIRTY] ?? 0}
            </Tooltip>
            <Tooltip text="Objects in sync">
              {getDirtyStateEmoji(DirtyStateType.SYNCING)}{" "}
              {dirtyStateCounts[DirtyStateType.SYNCING] ?? 0}
            </Tooltip>
            <Tooltip text="Failed syncs">
              {getDirtyStateEmoji(DirtyStateType.FAILED)}{" "}
              {dirtyStateCounts[DirtyStateType.FAILED] ?? 0}
            </Tooltip>
            <Tooltip text="Fast sync queue length">
              🐇 {fastSyncQueueLength}
            </Tooltip>
            <Tooltip text="Excess active periodic task handlers">
              ⏱️{" "}
              {
                // There will almost always be one periodic task handler
                // running, since these metrics are reported by the
                // periodic task handler. Only report the extra ones.
                Math.max(numActivePeriodicTasksHandlers - 1, 0)
              }
            </Tooltip>
            <Tooltip text="Active connections">🔌 {numActiveConns}</Tooltip>
            <Tooltip text="Download lock contention">
              📥 {downloadLockContention}
            </Tooltip>
            <Tooltip text="Sync lock contention">
              🔄 {syncLockContention}
            </Tooltip>
            <Tooltip text="User time">
              U: {(userTimeUsage * 100).toFixed(1)}%
            </Tooltip>
            <Tooltip text="System time">
              S: {(systemTimeUsage * 100).toFixed(1)}%
            </Tooltip>
            <div className="health-footer-bottom-spacer"></div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ServerHealthPage;
